import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateShort } from 'utils/DateUtils';
import axiosPrivate from 'config/axiosPrivate';
import ProjectAttributionCard from 'components/shared/ProjectAttributionCard';
import ProjectProgressChart from 'components/shared/ProjectProgressChart';
import './ProjectDetailsCard.css';

export function ProjectDetailsCard({
  order,
  isOpen,
  onToggle,
  onlineTextsCount = 0,
  totalTextsCount = 0,
  validatedTextsCount = 0,
  deliveredTextsCount = 0,
  draftTextsCount = 0,
  hideAttribution = false,
}) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(true);
  const isCardOpen =
    typeof isOpen === 'boolean' ? isOpen : internalOpen;
  const [isParamsOpen, setIsParamsOpen] = useState(true);
  const [isAdvancementOpen, setIsAdvancementOpen] = useState(true);
  const [isLinksOpen, setIsLinksOpen] = useState(true);
  const [isAttributionOpen, setIsAttributionOpen] = useState(true);
  const [isProgressOpen, setIsProgressOpen] = useState(true);
  const [aiAgentDetails, setAiAgentDetails] = useState(null);

  const formatContentType = (ct) => {
    const key = String(ct || '').toUpperCase();
    switch (key) {
      case 'LONG_CATEGORY':
        return t('Page catégorie');
      case 'PRODUCT':
        return t('Fiche produit');
      case 'STORE_LOCATOR':
        return t('Store locator');
      case 'GMB':
        return t('GBP');
      default:
        return ct || '';
    }
  };


  useEffect(() => {
    const fetchAiAgentDetails = async () => {
      const agentId = order?.aiAgentId;
      if (!agentId) {
        setAiAgentDetails(null);
        return;
      }

      try {
        const response = await axiosPrivate.get(`/smart-content/agent_ia/${agentId}`);
        if (response.data) {
          setAiAgentDetails(response.data);
        }
      } catch (error) {
        console.error('Error fetching AI agent details:', error);
        setAiAgentDetails(null);
      }
    };

    if (order && ((order?.type || '').toUpperCase() === 'SMART' || (order?.type || '').toUpperCase() === 'SMART_WITH_REVIEW')) {
      fetchAiAgentDetails();
    } else {
      setAiAgentDetails(null);
    }
  }, [order?.aiAgentId, order?.type]);

  if (!order) return null;

  const normalizedTextType = (order?.textType || '').toUpperCase();
  const contentTypeDisplay =
    normalizedTextType === 'EDITORIAL'
      ? t('Rédaction')
      : normalizedTextType === 'TRANSLATION'
        ? t('Traduction')
        : order?.contentTypeLabel || order?.type || '-';

  const containerStyle = {
    width: isCardOpen ? 400 : 'auto',
    backgroundColor: '#FAFAFA',
    borderRadius: isCardOpen ? 0 : 6,
    borderTop: isCardOpen ? 'none' : '1px solid #E5E7EB',
    borderRight: isCardOpen ? 'none' : '1px solid #E5E7EB',
    borderBottom: isCardOpen ? 'none' : '1px solid #E5E7EB',
    borderLeft: '1px solid #E5E7EB',
    overflow: 'hidden',
    marginTop: '-142px',
    display: 'inline-block',
    marginBottom: isCardOpen ? '-48px' : '10px',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: isCardOpen ? '10px 14px' : '6px 10px',
    borderBottom: isCardOpen ? '1px solid #F1F1F1' : 'none',
    backgroundColor: '#FAFAFA',
    cursor: 'pointer',
  };

  const iconStyle = {
    width: isCardOpen ? 32 : 40,
    height: isCardOpen ? 32 : 40,
  };

  return (
    <div style={containerStyle} className="project-details-card-container">
      <div
        style={headerStyle}
        className="project-details-card-header"
        onClick={() => {
          const next = !isCardOpen;
          if (onToggle) onToggle(next);
          if (typeof isOpen !== 'boolean') {
            setInternalOpen(next);
          }
        }}
      >
        <div className="project-details-card-header-inner" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <img
            src={
              isCardOpen
                ? '/Images/icon-menu/icone-close-menu.png'
                : '/Images/icon-menu/icon-open-menu.png'
            }
            alt="Details"
            style={iconStyle}
            className="project-details-card-header-icon"
          />
          <span className="project-details-card-header-title"
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: '#111827',
            }}
          >
            {t('Détails du projet')}
          </span>
        </div>
      </div>

      {isCardOpen && (
        <div className="project-details-card-body" style={{ padding: '10px 14px' }}>

          {/* ── SECTION : Paramètres du projet ── */}
          <div
            className="project-details-card-section-header project-details-card-params-header"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#4B5563',
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 6,
              cursor: 'pointer',
            }}
            onClick={() => setIsParamsOpen((prev) => !prev)}
          >
            <span className="project-details-card-section-title" style={{ fontSize: 14, fontWeight: 600 }}>
              {t('Paramètres du projet')}
            </span>
            <span
              className="project-details-card-section-chevron"
              style={{
                fontSize: 16,
                lineHeight: 1,
                color: '#6B7280',
                transform: isParamsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease-out',
              }}
            >
              ▾
            </span>
          </div>

          {isParamsOpen && (
            <div className="project-details-card-params-content">
              <div
                className="project-details-card-params-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)',
                  rowGap: 6,
                  columnGap: 12,
                  fontSize: 12,
                }}
              >
                <span className="project-details-card-param-label" style={{ color: '#6B7280' }}>{t('Type de contenu')}</span>
                <span className="project-details-card-param-value" style={{ color: '#111827' }}>{contentTypeDisplay}</span>

                <span className="project-details-card-param-label" style={{ color: '#6B7280' }}>{t('Mode de rédaction')}</span>
                <span className="project-details-card-param-value" style={{ color: '#111827' }}>
                  {order?.type === 'PREMIUM'
                    ? t('Rédacteur ')
                    : order?.type === 'SMART_WITH_REVIEW'
                      ? t('IA + Revue humaine')
                      : order?.type
                        ? t('IA')
                        : '-'}
                </span>

                <span className="project-details-card-param-label" style={{ color: '#6B7280' }}>{t('Nombre de textes')}</span>
                <span className="project-details-card-param-value" style={{ color: '#111827' }}>
                  {order?.numberOfTexts ??
                    order?.numberOfText ??
                    order?.textAmount ??
                    '-'}
                </span>

                <span className="project-details-card-param-label" style={{ color: '#6B7280' }}>{t('Nombre de mots')}</span>
                <span className="project-details-card-param-value" style={{ color: '#111827' }}>
                  {order?.numberOfWords ?? order?.maxWords
                    ? `${order?.numberOfWords ?? order?.maxWords} ${t('maximum')}`
                    : '-'}
                </span>

                <span className="project-details-card-param-label" style={{ color: '#6B7280' }}>{t('Langage')}</span>
                <span className="project-details-card-param-value" style={{ color: '#111827' }}>
                  {order?.language?.language || '-'}
                </span>

                <span className="project-details-card-param-label" style={{ color: '#6B7280' }}>{t('Chef de projet')}</span>
                <span className="project-details-card-param-value" style={{ color: '#111827' }}>
                  {order?.projectManager
                    ? `${order.projectManager.firstName || ''} ${order.projectManager.lastName || ''
                      }`.trim() || '-'
                    : '-'}
                </span>

                <span className="project-details-card-param-label" style={{ color: '#6B7280' }}>{t('Deadline')}</span>
                <span className="project-details-card-param-value" style={{ color: '#111827' }}>
                  {order?.deadline ? formatDateShort(order.deadline) : '-'}
                </span>

                <span className="project-details-card-param-label" style={{ color: '#6B7280' }}>{t('Options IA')}</span>
                <span className="project-details-card-param-value" style={{ color: '#111827' }}>
                  {order?.contentType
                    ? (String(order.contentType).toUpperCase() === 'OPTIMIZE'
                        ? t('Enrichissement')
                        : order.contentType)
                    : '-'}
                </span>

                <span className="project-details-card-param-label" style={{ color: '#6B7280' }}>{t('Génération d\'images')}</span>
                <span className="project-details-card-param-value" style={{ color: '#111827' }}>
                  {order?.numberOfImages
                    ? `${order.numberOfImages} ${t('Images / texte')}`
                    : '-'}
                </span>
              </div>

              {/* ── AI Agent block ── */}
              {((order?.type || '').toUpperCase() === 'SMART' || (order?.type || '').toUpperCase() === 'SMART_WITH_REVIEW') && (order?.aiAgentName || order?.aiAgentLabel || order?.aiAgent?.name || order?.aiAgentId) && (
                <>
                  <hr className="project-details-card-divider" style={{ width: '100%', margin: '12px 0', border: 'none', borderTop: '1px solid #F1F1F1' }} />
                  <div className="project-details-card-ai-agent-container">

                    <div className="project-details-card-ai-agent-header"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <img
                        src="/Images/ai-icon.png"
                        alt="AI Agent"
                        className="project-details-card-ai-agent-icon"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          flexShrink: 0,
                          marginTop: '-5px',
                        }}
                      />
                      <span className="project-details-card-ai-agent-label"
                        style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          fontWeight: 400,
                          marginTop: '-20px',
                        }}
                      >
                        {t('Agent AI utilisé')}
                      </span>
                    </div>

                    <div className="project-details-card-ai-agent-body"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        paddingLeft: '52px',
                      }}
                    >
                      <div className="project-details-card-ai-agent-name-row"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span className="project-details-card-ai-agent-name"
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#5E45E6',
                            marginTop: '-20px',
                          }}
                        >
                          {aiAgentDetails?.name ||
                            order?.aiAgent?.name ||
                            (order?.aiAgentId ? `Agent ${order.aiAgentId}` : '-')}
                        </span>

                        {normalizedTextType === 'TRANSLATION' && (aiAgentDetails?.langue || order?.aiAgent?.langue) && (
                          <>
                            <span className="project-details-card-ai-agent-separator" style={{ color: '#9CA3AF', fontSize: '14px', marginTop: '-20px' }}>|</span>
                            <span className="project-details-card-ai-agent-langue"
                              style={{
                                fontSize: '14px',
                                color: '#6B7280',
                                fontWeight: 400,
                                marginTop: '-20px',
                              }}
                            >
                              {aiAgentDetails?.langue || order?.aiAgent?.langue || '-'}
                            </span>
                          </>
                        )}

                        {aiAgentDetails?.typeContenu && (
                          <>
                            <span className="project-details-card-ai-agent-separator" style={{ color: '#9CA3AF', fontSize: '14px', marginTop: '-20px' }}>|</span>
                            <span className="project-details-card-ai-agent-type"
                              style={{
                                fontSize: '14px',
                                color: '#6B7280',
                                fontWeight: 400,
                                marginTop: '-20px',
                              }}
                            >
                              {formatContentType(aiAgentDetails.typeContenu)}
                            </span>
                          </>
                        )}

                        {!aiAgentDetails?.typeContenu && order?.aiAgent?.type && (
                          <>
                            <span className="project-details-card-ai-agent-separator" style={{ color: '#9CA3AF', fontSize: '14px' }}>|</span>
                            <span className="project-details-card-ai-agent-type"
                              style={{
                                fontSize: '14px',
                                color: '#6B7280',
                                fontWeight: 400,
                              }}
                            >
                              {order.aiAgent.type}
                            </span>
                          </>
                        )}
                      </div>

                      <hr className="project-details-card-ai-agent-divider"
                        style={{
                          width: '125%',
                          margin: '0px 0 1px -60px',
                          border: 'none',
                          borderTop: '1px solid rgb(156, 153, 153)',
                        }}
                      />

                      {/* Modèle */}
                      {(order?.aiName || order?.aiAgentName || order?.llmType || aiAgentDetails?.llmType) && (
                        <div className="project-details-card-ai-agent-model"
                          style={{
                            fontSize: '13px',
                            color: '#4B5563',
                            marginLeft: '-50px',
                            fontWeight: 450,
                            marginTop: '-5px',
                          }}
                        >
                          {t('Modèle')} →
                          <span className="project-details-card-ai-agent-model-value"
                            style={{
                              color: '#1B0F3FBF',
                              fontWeight: 600,
                            }}
                          >
                            {aiAgentDetails?.llmType || order?.llmType || '-'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── SECTION : Avancement ── */}
          <div
            className="project-details-card-section-header project-details-card-advancement-header"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#4B5563',
              marginTop: 12,
              paddingTop: 10,
              borderTop: '1px solid #F1F1F1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 6,
              cursor: 'pointer',
            }}
            onClick={() => setIsAdvancementOpen((prev) => !prev)}
          >
            <span className="project-details-card-section-title" style={{ fontSize: 14, fontWeight: 600 }}>
              {t('Avancement')}
            </span>
            <span
              className="project-details-card-section-chevron"
              style={{
                fontSize: 16,
                lineHeight: 1,
                color: '#6B7280',
                transform: isAdvancementOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease-out',
              }}
            >
              ▾
            </span>
          </div>

          {isAdvancementOpen && (
            <div className="project-details-card-advancement-content" style={{ marginTop: 8 }}>
              {[
                {
                  label: t('Textes en brouillons'),
                  value: draftTextsCount,
                  total: totalTextsCount,
                  color: '#CFCFCF',
                  className: 'project-details-card-advancement-row--draft',
                },
                {
                  label: t('Textes livrés'),
                  value: deliveredTextsCount,
                  total: totalTextsCount,
                  color: '#FFD44A',
                  className: 'project-details-card-advancement-row--delivered',
                },
                {
                  label: t('Textes validés'),
                  value: validatedTextsCount,
                  total: totalTextsCount,
                  color: '#3FBF9A',
                  className: 'project-details-card-advancement-row--validated',
                },
                {
                  label: t('Textes en ligne'),
                  value: onlineTextsCount,
                  total: totalTextsCount,
                  color: '#8180F9',
                  className: 'project-details-card-advancement-row--online',
                },
              ].map((item, idx) => {
                const safeTotal = item.total || 0;
                const percent =
                  safeTotal > 0
                    ? Math.min(100, Math.max(0, (item.value / safeTotal) * 100))
                    : 0;
                return (
                  <div key={idx} className={`project-details-card-advancement-row ${item.className}`} style={{ marginBottom: idx === 3 ? 0 : 10 }}>
                    <div
                      className="project-details-card-advancement-row-header"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: 12,
                        marginBottom: 10,
                      }}
                    >
                      <span className="project-details-card-advancement-row-label" style={{ color: '#4B5563' }}>{item.label}</span>
                      <span className="project-details-card-advancement-row-count" style={{ color: '#111827', fontWeight: 500 }}>
                        {item.value || 0} / {safeTotal}
                      </span>
                    </div>
                    <div
                      className="project-details-card-advancement-bar-track"
                      style={{
                        height: 6,
                        borderRadius: 999,
                        backgroundColor: '#F3F4F6',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        className="project-details-card-advancement-bar-fill"
                        style={{
                          width: `${percent}%`,
                          height: '100%',
                          backgroundColor: item.color,
                          borderRadius: 999,
                          transition: 'width 0.25s ease-out',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── SECTION : Liens utiles ── */}
          <div
            className="project-details-card-section-header project-details-card-links-header"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#4B5563',
              marginTop: 12,
              paddingTop: 10,
              borderTop: '1px solid #F1F1F1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 6,
              cursor: 'pointer',
            }}
            onClick={() => setIsLinksOpen((prev) => !prev)}
          >
            <span className="project-details-card-section-title" style={{ fontSize: 14, fontWeight: 600 }}>
              {t('Liens utiles')}
            </span>
            <span
              className="project-details-card-section-chevron"
              style={{
                fontSize: 16,
                lineHeight: 1,
                color: '#6B7280',
                transform: isLinksOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease-out',
              }}
            >
              ▾
            </span>
          </div>

          {isLinksOpen && (
            <div className="project-details-card-links-content" style={{ marginTop: 8, fontSize: 12 }}>

              {/* Brief détaillé */}
              <div
                className="project-details-card-link-row project-details-card-link-row--brief"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto auto 1fr',
                  columnGap: 4,
                  alignItems: 'center',
                  marginBottom: 4,
                }}
              >
                <span
                  className="project-details-card-link-label"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    fontWeight: 400,
                  }}
                >
                  <img
                    src="/Images/icon-lien.png"
                    alt="Brief"
                    className="project-details-card-link-icon"
                    style={{ width: 16, height: 16 }}
                  />
                  {t('Brief détaillé')}
                </span>
                <span className="project-details-card-link-separator" style={{ fontSize: 12, color: '#818181' }}>-</span>
                <span
                  className="project-details-card-link-value"
                  style={{
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {order?.detailedBrief ? (
                    <a
                      href={order.detailedBrief}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-details-link"
                      title={order.detailedBrief}
                    >
                      {order.detailedBrief}
                    </a>
                  ) : (
                    <span
                      className="project-details-card-link-empty"
                      style={{
                        color: '#B6B6B6',
                        fontSize: 12,
                        fontWeight: 400,
                      }}
                    >
                      Non renseignée
                    </span>
                  )}
                </span>
              </div>

              {/* Gap sémantique */}
              <div
                className="project-details-card-link-row project-details-card-link-row--semantic"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto auto 1fr',
                  columnGap: 4,
                  alignItems: 'center',
                }}
              >
                <span
                  className="project-details-card-link-label"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    color: '#818181',
                    fontSize: 12,
                    fontWeight: 400,
                  }}
                >
                  <img
                    src="/Images/icon-lien.png"
                    alt="Gap sémantique"
                    className="project-details-card-link-icon"
                    style={{ width: 16, height: 16 }}
                  />
                  {t('Gap sémantique')}
                </span>
                <span className="project-details-card-link-separator" style={{ fontSize: 12, color: '#818181' }}>-</span>
                <span
                  className="project-details-card-link-value"
                  style={{
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {order?.semanticGap ? (
                    <a
                      href={order.semanticGap}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-details-link"
                      title={order.semanticGap}
                    >
                      {order.semanticGap}
                    </a>
                  ) : (
                    <span
                      className="project-details-card-link-empty"
                      style={{
                        color: '#B6B6B6',
                        fontSize: 12,
                        fontWeight: 400,
                      }}
                    >
                      Non renseignée
                    </span>
                  )}
                </span>
              </div>
            </div>
          )}

          {/* ── SECTION : Progression / Attribution (selon type) ── */}
          {((order?.type || '').toUpperCase() === 'SMART' ||
            (order?.type || '').toUpperCase() === 'SMART_WITH_REVIEW') ? (

            <div className="project-details-card-smart-section">
              <div
                className="project-details-card-section-header project-details-card-progress-header"
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#4B5563',
                  marginTop: 12,
                  paddingTop: 10,
                  borderTop: '1px solid #F1F1F1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 6,
                  cursor: 'pointer',
                }}
                onClick={() => setIsProgressOpen((prev) => !prev)}
              >
                <span className="project-details-card-section-title" style={{ fontSize: 14, fontWeight: 600 }}>
                  {t('Progression')}
                </span>
                <span
                  className="project-details-card-section-chevron"
                  style={{
                    fontSize: 16,
                    lineHeight: 1,
                    color: '#6B7280',
                    transform: isProgressOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease-out',
                  }}
                >
                  ▾
                </span>
              </div>

              {isProgressOpen && (
                <div className="project-details-card-progress-content" style={{ marginTop: 8 }}>
                  <ProjectProgressChart
                    total={totalTextsCount}
                    draft={draftTextsCount}
                    validated={validatedTextsCount}
                    delivered={deliveredTextsCount}
                    online={onlineTextsCount}
                    deadline={order?.deadline}
                  />
                </div>
              )}
            </div>

          ) : (

            <div className="project-details-card-premium-section">
              {!hideAttribution && (
                <div className="project-details-card-attribution-section">
                  <div
                    className="project-details-card-section-header project-details-card-attribution-header"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#4B5563',
                      marginTop: 12,
                      paddingTop: 10,
                      borderTop: '1px solid #F1F1F1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 6,
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsAttributionOpen((prev) => !prev)}
                  >
                    <span className="project-details-card-section-title" style={{ fontSize: 14, fontWeight: 600 }}>
                      {t('Attribution')}
                    </span>
                    <span
                      className="project-details-card-section-chevron"
                      style={{
                        fontSize: 16,
                        lineHeight: 1,
                        color: '#6B7280',
                        transform: isAttributionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.15s ease-out',
                      }}
                    >
                      ▾
                    </span>
                  </div>

                  {isAttributionOpen && (
                    <div className="project-details-card-attribution-content" style={{ marginTop: 8 }}>
                      <ProjectAttributionCard order={order} />
                    </div>
                  )}
                </div>
              )}

              {/* Section Progression */}
              <div className="project-details-card-progress-section">
                <div
                  className="project-details-card-section-header project-details-card-progress-header"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#4B5563',
                    marginTop: 12,
                    paddingTop: 10,
                    borderTop: '1px solid #F1F1F1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 6,
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsProgressOpen((prev) => !prev)}
                >
                  <span className="project-details-card-section-title" style={{ fontSize: 14, fontWeight: 600 }}>
                    {t('Progression')}
                  </span>
                  <span
                    className="project-details-card-section-chevron"
                    style={{
                      fontSize: 16,
                      lineHeight: 1,
                      color: '#6B7280',
                      transform: isProgressOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.15s ease-out',
                    }}
                  >
                    ▾
                  </span>
                </div>

                {isProgressOpen && (
                  <div className="project-details-card-progress-content" style={{ marginTop: 8 }}>
                    <ProjectProgressChart
                      total={totalTextsCount}
                      draft={draftTextsCount}
                      validated={validatedTextsCount}
                      delivered={deliveredTextsCount}
                      online={onlineTextsCount}
                      deadline={order?.deadline}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <></>
        </div>
      )}
    </div>
  );
}

export default ProjectDetailsCard;